import { create } from "@bufbuild/protobuf";
import { BrandKitSchema, ColorSchema, ColorType, FontOrigin, FontUseCase, ImageFormat, ImageTheme, ImageType, type BrandKit, type Color, type Font, type Image } from "#gen/brands/v1/service_pb.js";
import type { LandingPage, PageContainer } from "#gen/bundles/v1/bundle_pb.js";
import type { Page } from "#gen/pages/v2/page_pb.js";
import { FcColorWhite } from "#lib/code.js";
import { stringValue } from "#lib/struct.js";

const DEFAULT_DARK_TEXT_COLOR = "#111827";
const DEFAULT_CUSTOM_PRIMARY_BUTTON_COLOR = "#374151";
const DEFAULT_CUSTOM_SECONDARY_BUTTON_COLOR = "#d1d5db";
const DEFAULT_BACKGROUND_STYLE: BackgroundStyleObject = { url: "", position: "center", repeat: "no-repeat", size: "cover", attachment: "scroll" };

export const DEFAULT_THEME_COLORS = {
	default: {
		headerBgColor: "#d1d5db",
		headerTextColor: "#111827",
		paragraphTextColor: "#111827",
		primaryButtonBgColor: "#374151",
		primaryButtonTextColor: "#ffffff",
		secondaryButtonBgColor: "#d1d5db",
		secondaryButtonTextColor: "#111827",
	},
	legacy: {
		bgColor: "#ffffff",
		headerTextColor: "#ffffff",
		paragraphTextColor: "#ffffff",
		primaryButtonBgColor: "#ffffff",
		primaryButtonTextColor: "#111827",
		secondaryButtonBgColor: "#ffffff",
		secondaryButtonTextColor: "#111827",
	},
	// Mirrors what PAGE_V2 components actually render so editor swatches match the page.
	pageV2: {
		headerTextColor: "#111928",
		paragraphTextColor: "#111928",
		primaryButtonBgColor: "#ffffff",
		primaryButtonTextColor: "#000000",
	},
};

export type Themeable = LandingPage | PageContainer | Page;

const DEFAULT_BRAND_KIT: BrandKit = create(BrandKitSchema, {
	primaryColors: [
		create(ColorSchema, {
			hex: DEFAULT_CUSTOM_PRIMARY_BUTTON_COLOR,
		}),
	],
	secondaryColors: [
		create(ColorSchema, {
			hex: DEFAULT_CUSTOM_SECONDARY_BUTTON_COLOR,
		}),
	],
	tertiaryColors: [
		create(ColorSchema, {
			hex: FcColorWhite,
		}),
	],
});

function parseColor(color: string): number[] | null {
	let c = (color ?? "").trim().toLowerCase();
	if (c === "transparent") return null;
	if (c[0] === "#") c = c.slice(1);
	if (/^[0-9a-f]{3}$/.test(c)) c = c.replace(/./g, ch => ch + ch); // expand #rgb shorthand
	if (/^[0-9a-f]{8}$/.test(c)) c = c.slice(0, 6); // drop the alpha byte before the luma and shade math
	if (!/^[0-9a-f]{6}$/.test(c)) return null; // "", unknown, or malformed
	const rgb = Number.parseInt(c, 16);
	return [(rgb >> 16) & 0xff, (rgb >> 8) & 0xff, rgb & 0xff];
}

// Alpha from 0 to 1 for an #RRGGBBAA value or the transparent keyword. Returns 1 when the color is opaque.
function parseAlpha(color: string): number {
	let c = (color ?? "").trim().toLowerCase();
	if (c === "transparent") return 0;
	if (c[0] === "#") c = c.slice(1);
	if (/^[0-9a-f]{8}$/.test(c)) return Number.parseInt(c.slice(6, 8), 16) / 255;
	return 1;
}

function alphaSuffix(color: string): string {
	let c = (color ?? "").trim().toLowerCase();
	if (c[0] === "#") c = c.slice(1);
	return /^[0-9a-f]{8}$/.test(c) ? c.slice(6, 8) : "";
}

export function lightenDarkenColor(color: string, amt: number): string {
	const rgb = parseColor(color);
	if (!rgb) return color; // cannot shade a transparent or unknown color so leave it as is
	const [r, g, b] = rgb.map(channel => Math.min(255, Math.max(0, channel + amt)));
	return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0") + alphaSuffix(color); // preserve alpha
}

export function isLight(color: string): boolean {
	if (parseAlpha(color) < 0.5) return true; // a mostly transparent color sits over a light backdrop so we want dark text
	const rgb = parseColor(color);
	if (!rgb) return true; // treat a transparent or unknown color as light so auto text stays dark
	const [r, g, b] = rgb;
	const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
	return luma >= 128;
}

export function getBackgroundColor(brandKit: BrandKit | undefined, landingPage?: Themeable): string {
	const theme = landingPage?.theme || "custom";
	return stringValue(landingPage?.themeOverrides, "backgroundColor") ?? (theme === "base" || theme === "custom" ? DEFAULT_THEME_COLORS.legacy.bgColor : (getSecondaryColor(brandKit)?.hex ?? "transparent"));
}

export function getHeaderBackgroundColor(brandKit: BrandKit | undefined, landingPage: Themeable | undefined): string {
	const theme = landingPage?.theme || "custom";
	return stringValue(landingPage?.themeOverrides, "headerBackgroundColor") ?? (theme === "base" || theme === "custom" ? DEFAULT_THEME_COLORS.default.headerBgColor : (getSecondaryColor(brandKit)?.hex ?? FcColorWhite));
}

// The colour the browser paints its own chrome with, via the theme-color meta on older iOS and by
// sampling the page on newer iOS. Classic, modern and profile lay a header band across the top of
// the viewport, and the appearance editor offers "Header background" for them, so that band is what
// the chrome should match.
const HEADER_BAND_LAYOUTS = new Set(["classic", "modern", "profile"]);

export function getBrowserChromeColor(brandKit: BrandKit | undefined, themeable: Themeable | undefined, layoutName: string = getLayoutName(themeable)): string {
	return HEADER_BAND_LAYOUTS.has(layoutName) ? getHeaderBackgroundColor(brandKit, themeable) : getBackgroundColor(brandKit, themeable);
}

export function getHeaderTextColor(brandKit: BrandKit | undefined, landingPage: Themeable | undefined): string {
	const layout = getLayoutName(landingPage) ?? "legacy";
	const theme = landingPage?.theme || "custom";
	const defaultThemeColors = defaultThemeColorsFor(landingPage);
	if (theme === "custom" || getLayoutName(landingPage) === "") {
		return stringValue(landingPage?.themeOverrides, "headerTextColor") ?? defaultThemeColors.headerTextColor;
	}
	if (layout !== "classic" && layout !== "profile") {
		return stringValue(landingPage?.themeOverrides, "headerTextColor") ?? (layout === "legacy" ? defaultThemeColors.headerTextColor : getTextColor(getHeaderBackgroundColor(brandKit, landingPage)));
	}
	return defaultThemeColors.headerTextColor;
}

export function getHeaderBackgroundImage(landingPage: Themeable | undefined): string {
	return stringValue(landingPage?.themeOverrides, "headerBackgroundImage") ?? "";
}

export function getBackgroundImage(landingPage: Themeable | undefined): string {
	return stringValue(landingPage?.themeOverrides, "backgroundImage") ?? "";
}

export interface BackgroundStyleObject {
	url: string;
	position: string;
	repeat: string;
	size: string;
	attachment: string;
}

// The stored value is arbitrary JSON and this runs on the render path, so a throw would blank the page.
// Callers get a copy so nobody can mutate the shared defaults.
export function parseBackgroundStyleString(styleString: string): BackgroundStyleObject {
	const s = styleString?.trim() ?? "";
	if (!s.length) return { ...DEFAULT_BACKGROUND_STYLE };
	let o: Partial<BackgroundStyleObject> | undefined;
	try {
		o = JSON.parse(s) as Partial<BackgroundStyleObject>;
	} catch {
		return { ...DEFAULT_BACKGROUND_STYLE };
	}
	if (o && typeof o.url === "string") {
		return {
			url: o.url,
			position: o.position ?? DEFAULT_BACKGROUND_STYLE["position"],
			repeat: o.repeat ?? DEFAULT_BACKGROUND_STYLE["repeat"],
			size: o.size ?? DEFAULT_BACKGROUND_STYLE["size"],
			attachment: o.attachment ?? DEFAULT_BACKGROUND_STYLE["attachment"],
		};
	}
	return { ...DEFAULT_BACKGROUND_STYLE };
}

export interface BackgroundLayerVars {
	pin: "static" | "sticky";
	/** The box background-size resolves against. */
	height: "100dvh" | "100%";
}

// Sticky replaces background-attachment fixed, which iOS Safari resolves against a stale viewport box.
// The height is dynamic rather than large because the consumer layout holds the document still, so the
// browser chrome never retracts.
export function backgroundLayerVars(style: BackgroundStyleObject): BackgroundLayerVars {
	// Only an explicit fixed attachment pins the layer.
	if (style.attachment === "fixed") {
		// A sticky box as tall as its container has no room to move, so a pinned layer is never content sized.
		return { pin: "sticky", height: "100dvh" };
	}
	// Cover fills whatever box it is given, so a screen-tall box still leaves it scrolling away on a page
	// taller than one screen, exposing a band of the page color underneath. Sizing it to the content box
	// instead, same as a vertically repeating background, keeps it covering the whole page as it scrolls.
	const fillsWholePage = style.size === "cover" || style.repeat === "repeat" || style.repeat === "repeat-y";
	return { pin: "static", height: fillsWholePage ? "100%" : "100dvh" };
}

// The white content card fills the fullBleed tray, so the page background only
// shows where the card does not. Returns true when the page paints something
// distinct from that card, an image or an opaque non white color, so the tray
// frames it.
export function hasBackgroundFill(brandKit: BrandKit | undefined, themeable?: Themeable): boolean {
	if (parseBackgroundStyleString(getBackgroundImage(themeable)).url) {
		return true;
	}
	const backgroundColor = getBackgroundColor(brandKit, themeable);
	if (parseAlpha(backgroundColor) < 0.5) {
		return false;
	}
	const rgb = parseColor(backgroundColor);
	return rgb ? !(rgb[0] === 255 && rgb[1] === 255 && rgb[2] === 255) : false;
}

export function getPrimaryButtonBackgroundColor(brandKit: BrandKit | undefined, landingPage: Themeable | undefined): string {
	const theme = landingPage?.theme || "custom";
	const defaultThemeColors = defaultThemeColorsFor(landingPage);
	if (theme === "custom" || getLayoutName(landingPage) === "") {
		const brandPrimary = usesBrandKitButtonColor(landingPage) ? getPrimaryColor(brandKit)?.hex : undefined;
		return stringValue(landingPage?.themeOverrides, "primaryButtonBackgroundColor") ?? brandPrimary ?? defaultThemeColors.primaryButtonBgColor;
	}
	return stringValue(landingPage?.themeOverrides, "primaryButtonBackgroundColor") ?? getPrimaryColor(brandKit)?.hex ?? FcColorWhite;
}

export function getPrimaryButtonTextColor(brandKit: BrandKit | undefined, landingPage: Themeable | undefined): string {
	const theme = landingPage?.theme || "custom";
	const defaultThemeColors = defaultThemeColorsFor(landingPage);
	if (theme === "custom" || getLayoutName(landingPage) === "") {
		const brandPrimary = usesBrandKitButtonColor(landingPage) ? getPrimaryColor(brandKit)?.hex : undefined;
		return stringValue(landingPage?.themeOverrides, "primaryButtonTextColor") ?? (brandPrimary ? getTextColor(brandPrimary) : defaultThemeColors.primaryButtonTextColor);
	}
	return stringValue(landingPage?.themeOverrides, "primaryButtonTextColor") ?? getTextColor(getPrimaryColor(brandKit)?.hex ?? defaultThemeColors.primaryButtonBgColor);
}

export function getSecondaryButtonBackgroundColor(brandKit: BrandKit | undefined, landingPage: Themeable | undefined): string {
	const theme = landingPage?.theme || "custom";
	const defaultThemeColors = getDefaultThemeColors(getLayoutName(landingPage));
	return stringValue(landingPage?.themeOverrides, "secondaryButtonBackgroundColor") ?? (theme === "custom" || getLayoutName(landingPage) === "" ? defaultThemeColors.secondaryButtonBgColor : (getSecondaryColor(brandKit)?.hex ?? FcColorWhite));
}

export function getSecondaryButtonTextColor(brandKit: BrandKit | undefined, landingPage: Themeable | undefined): string {
	const theme = landingPage?.theme || "custom";
	const defaultThemeColors = getDefaultThemeColors(getLayoutName(landingPage));
	return stringValue(landingPage?.themeOverrides, "secondaryButtonTextColor") ?? (theme === "custom" || getLayoutName(landingPage) === "" ? defaultThemeColors.secondaryButtonTextColor : getTextColor(getSecondaryButtonBackgroundColor(brandKit, landingPage)));
}

export function getTextColor(background: string): string {
	return isLight(background) ? DEFAULT_DARK_TEXT_COLOR : FcColorWhite;
}

export function getParagraphTextColor(brandKit: BrandKit | undefined, landingPage: Themeable | undefined): string {
	const layout = getLayoutName(landingPage) ?? "legacy";
	const theme = landingPage?.theme || "custom";
	const defaultThemeColors = defaultThemeColorsFor(landingPage);
	if (theme === "custom" || getLayoutName(landingPage) === "") {
		return stringValue(landingPage?.themeOverrides, "paragraphTextColor") ?? defaultThemeColors.paragraphTextColor;
	}
	if (layout !== "classic" && layout !== "profile") {
		return stringValue(landingPage?.themeOverrides, "paragraphTextColor") ?? (layout === "legacy" ? defaultThemeColors.paragraphTextColor : getTextColor(getHeaderBackgroundColor(brandKit, landingPage)));
	}
	return defaultThemeColors.paragraphTextColor;
}

function isPageV2(landingPage: Themeable | undefined): landingPage is Page {
	return Boolean(landingPage && "components" in landingPage && Array.isArray((landingPage as Page).components));
}

function usesBrandKitButtonColor(t: Themeable | undefined): boolean {
	return t === undefined || isPageV2(t);
}

// A custom font name is spliced into one flat inline style string. CSS will not close a
// declaration on ";" while a "(" is still unmatched, so a scraped `var(--foo` swallows
// every later custom property (background color/image included). Fall back instead.
function fontDeclarationValue(font: Font | undefined): string {
	if (!font) return "inherit";
	const raw = font.origin == FontOrigin.GOOGLE ? font.originId.replace("+", " ") : font.name;
	let depth = 0;
	for (const char of raw) {
		if (char === "(") depth++;
		else if (char === ")") {
			depth--;
			if (depth < 0) return "inherit";
		}
	}
	return depth === 0 ? raw : "inherit";
}

export function styleWithBrandKit(brandKit: BrandKit | undefined, landingPage?: Themeable): string {
	const logo = getPrimaryLogo(brandKit);
	const font = getBodyFont(brandKit);
	const headerFont = getHeaderFont(brandKit);
	const buttonFont = getButtonFont(brandKit);

	const primaryColor = getPrimaryColor(brandKit);

	const vars: string[] = [];
	if (logo) {
		vars.push(`--logo: url(${logo?.url});`);
	}
	if (font) {
		vars.push(`--font: ${fontDeclarationValue(font)};`);
	}
	vars.push(`--primary-color: ${primaryColor?.hex ?? DEFAULT_DARK_TEXT_COLOR};`);
	vars.push(`--secondary-color: ${getSecondaryColor(brandKit)?.hex ?? "white"};`);

	vars.push(`--background-color: ${getBackgroundColor(brandKit, landingPage)};`);
	const primaryButtonBackgroundColor = getPrimaryButtonBackgroundColor(brandKit, landingPage);
	const primaryButtonHoverColor = lightenDarkenColor(primaryButtonBackgroundColor, isLight(primaryButtonBackgroundColor) ? -20 : 20);
	vars.push(`--primary-button-background-color: ${primaryButtonBackgroundColor};`);
	vars.push(`--primary-button-color: ${getPrimaryButtonTextColor(brandKit, landingPage)};`);
	vars.push(`--primary-button-hover-color: ${primaryButtonHoverColor};`);
	vars.push(`--primary-button-focus-color: ${primaryButtonHoverColor};`);
	vars.push(`--primary-button-ring-color: ${primaryButtonHoverColor};`);

	const secondaryButtonBackgroundColor = getSecondaryButtonBackgroundColor(brandKit, landingPage);
	const secondaryButtonHoverColor = lightenDarkenColor(secondaryButtonBackgroundColor, isLight(secondaryButtonBackgroundColor) ? -20 : 20);
	vars.push(`--secondary-button-background-color: ${secondaryButtonBackgroundColor};`);
	vars.push(`--secondary-button-color: ${getSecondaryButtonTextColor(brandKit, landingPage)};`);
	vars.push(`--secondary-button-hover-color: ${secondaryButtonHoverColor};`);
	vars.push(`--secondary-button-focus-color: ${secondaryButtonHoverColor};`);
	vars.push(`--secondary-button-ring-color: ${secondaryButtonHoverColor};`);

	vars.push(`--header-font: ${fontDeclarationValue(headerFont)};`);
	if (buttonFont) {
		vars.push(`--button-font: ${fontDeclarationValue(buttonFont)};`);
	}
	vars.push(`--header-color: ${getHeaderTextColor(brandKit, landingPage)};`);
	const paragraphOverride = stringValue(landingPage?.themeOverrides, "paragraphTextColor");
	if (paragraphOverride) {
		vars.push(`--paragraph-color: ${paragraphOverride};`);
	}
	const headerBgImage = getHeaderBackgroundImage(landingPage);
	vars.push(`--header-background-image: ${headerBgImage ? `url(${headerBgImage})` : "none"};`);
	const bgStyle = parseBackgroundStyleString(getBackgroundImage(landingPage));
	vars.push(`--background-image: ${bgStyle.url ? `url(${bgStyle.url})` : "none"};`);
	vars.push(`--background-position: ${bgStyle.position};`);
	vars.push(`--background-repeat: ${bgStyle.repeat};`);
	vars.push(`--background-size: ${bgStyle.size};`);
	vars.push(`--background-attachment: ${bgStyle.attachment};`);
	const bgLayer = backgroundLayerVars(bgStyle);
	vars.push(`--background-pin: ${bgLayer.pin};`);
	vars.push(`--background-layer-height: ${bgLayer.height};`);
	vars.push(`--header-background-color: ${getHeaderBackgroundColor(brandKit, landingPage)};`);

	return vars.join(" ");
}

export function getPrimaryIcon(brandKit?: BrandKit, theme: ImageTheme = ImageTheme.DARK): Image {
	if (!brandKit) {
		brandKit = DEFAULT_BRAND_KIT;
	}
	const images = [...(brandKit.primaryImages ?? [])];
	return (
		images.find(value => {
			return value.type == ImageType.ICON && value.theme == theme;
		}) ?? images[0]
	);
}

export function getPrimaryLogo(brandKit?: BrandKit, theme: ImageTheme = ImageTheme.DARK): Image | undefined {
	if (!brandKit) {
		brandKit = DEFAULT_BRAND_KIT;
	}
	const images = [...(brandKit.primaryImages ?? [])];
	images.sort((a, b) => {
		const aspectA = a.width / a.height;
		const aspectB = b.width / b.height;

		return aspectA > aspectB ? -1 : aspectA == aspectB ? 0 : 1;
	});
	return (
		images.find(value => {
			return value.type == ImageType.LOGO && value.format == ImageFormat.SVG && value.theme == theme;
		}) ?? images[0]
	);
}

export function getHeaderFont(brandKit: BrandKit | undefined): Font | undefined {
	if (!brandKit) {
		brandKit = DEFAULT_BRAND_KIT;
	}
	const fonts = [...(brandKit.fonts ?? [])];
	return fonts.find(value => {
		return value.useCase == FontUseCase.HEADER;
	});
}

export function getBodyFont(brandKit: BrandKit | undefined): Font | undefined {
	if (!brandKit) {
		brandKit = DEFAULT_BRAND_KIT;
	}
	const fonts = [...(brandKit.fonts ?? [])];
	return fonts.find(value => {
		return value.useCase == FontUseCase.BODY;
	});
}

export function getButtonFont(brandKit: BrandKit | undefined): Font | undefined {
	if (!brandKit) {
		brandKit = DEFAULT_BRAND_KIT;
	}
	const fonts = [...(brandKit.fonts ?? [])];
	return fonts.find(value => {
		return value.useCase == FontUseCase.BUTTON;
	});
}

export function getPrimaryColor(brandKit?: BrandKit, type: ColorType = ColorType.DARK): Color | undefined {
	if (!brandKit) {
		brandKit = DEFAULT_BRAND_KIT;
	}
	const colors = [...(brandKit.primaryColors ?? [])];
	if (colors.length == 0) {
		return undefined;
	}
	return (
		colors.find(value => {
			return value.type == type;
		}) ?? colors[0]
	);
}

export function getSecondaryColor(brandKit?: BrandKit, type: ColorType = ColorType.DARK): Color | undefined {
	if (!brandKit) {
		brandKit = DEFAULT_BRAND_KIT;
	}
	const colors = [...(brandKit.secondaryColors ?? [])];
	if (colors.length == 0) {
		return undefined;
	}
	return (
		colors.find(value => {
			return value.type == type;
		}) ?? colors[0]
	);
}

export function getTertiaryColor(brandKit?: BrandKit, type: ColorType = ColorType.DARK): Color | undefined {
	if (!brandKit) {
		brandKit = DEFAULT_BRAND_KIT;
	}
	const colors = [...(brandKit.tertiaryColors ?? [])];
	if (colors.length == 0) {
		return undefined;
	}
	return (
		colors.find(value => {
			return value.type == type;
		}) ?? colors[0]
	);
}

export function getDefaultThemeColors(layoutName?: string) {
	if (!layoutName || layoutName === "legacy") return DEFAULT_THEME_COLORS.legacy;
	else return DEFAULT_THEME_COLORS.default;
}

// Page Builder v2 has no layoutName, so getLayoutName() reports "legacy" for it. Route it
// to its own palette so the editor's default swatches and default-detection line up with
// the components' rendered colors.
function defaultThemeColorsFor(t: Themeable | undefined) {
	return isPageV2(t) ? DEFAULT_THEME_COLORS.pageV2 : getDefaultThemeColors(getLayoutName(t));
}

export function getLayoutName(t: Themeable | undefined): string {
	if (t == null) return "legacy";
	if ("layoutName" in t) return t.layoutName ?? "legacy";
	return "legacy";
}

export function styleString(vars: Record<string, string | undefined>): string {
	return Object.entries(vars)
		.filter(([, v]) => v)
		.map(([k, v]) => `${k}: ${v};`)
		.join(" ");
}
